import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../components/ui/use-toast";
import { ArrowLeft, Save, MapPin, Calendar, Building2, AlertCircle } from "lucide-react";

export default function ProjectDetailsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "on_hold", label: "On Hold" },
    { value: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    if (isAuthenticated && projectId) {
      fetchProject();
    }
  }, [isAuthenticated, projectId]);

  const fetchProject = async () => {
    try {
      const res = await api.get(`/api/projects/${projectId}/`);
      setProject(res.data);
      setFormData({
        latitude: res.data.latitude || "",
        longitude: res.data.longitude || "",
        status: res.data.status || "active",
        description: res.data.description || "",
      });
    } catch (err) {
      let errorMessage = "Failed to load project";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 403) {
        errorMessage = "You do not have permission to view this project";
      } else if (err.response?.status === 404) {
        errorMessage = "Project not found";
      }
      
      toast({
        title: "Error Loading Project",
        description: errorMessage,
        variant: "destructive",
      });
      
      if (err.response?.status === 404) {
        navigate("/projects");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/projects/${projectId}/`, formData);
      toast({
        title: "Success",
        description: "Project updated successfully",
      });
      setEditing(false);
      fetchProject(); // Refresh data
    } catch (err) {
      let errorMessage = "Failed to update project";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.latitude) {
        errorMessage = `Latitude error: ${err.response.data.latitude.join(', ')}`;
      } else if (err.response?.data?.longitude) {
        errorMessage = `Longitude error: ${err.response.data.longitude.join(', ')}`;
      } else if (err.response?.data?.status) {
        errorMessage = `Status error: ${err.response.data.status.join(', ')}`;
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid project data provided";
      } else if (err.response?.status === 403) {
        errorMessage = "You do not have permission to modify this project";
      } else if (err.response?.status === 404) {
        errorMessage = "Project not found";
      }
      
      toast({
        title: "Project Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      latitude: project?.latitude || "",
      longitude: project?.longitude || "",
      status: project?.status || "active",
      description: project?.description || "",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">Please log in to view project details.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate("/projects")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Project Details</h1>
      </div>

      {/* Project Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Project Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Project Name - Read Only */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Project Name</Label>
            <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
              {project.name}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Project name cannot be modified once created
            </p>
          </div>

          {/* Status */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Status</Label>
            {editing ? (
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  project.status === "active" ? "bg-green-100 text-green-800" :
                  project.status === "completed" ? "bg-blue-100 text-blue-800" :
                  project.status === "on_hold" ? "bg-yellow-100 text-yellow-800" :
                  project.status === "cancelled" ? "bg-red-100 text-red-800" :
                  "bg-gray-100 text-gray-800"
                }`}>
                  {statusOptions.find(s => s.value === project.status)?.label || project.status || "Unknown"}
                </span>
              </div>
            )}
          </div>

          {/* Coordinates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Latitude</Label>
              {editing ? (
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="Enter latitude"
                  className="mt-1"
                />
              ) : (
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                  {project.latitude || "Not set"}
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Longitude</Label>
              {editing ? (
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="Enter longitude"
                  className="mt-1"
                />
              ) : (
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                  {project.longitude || "Not set"}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Description</Label>
            {editing ? (
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter project description"
                className="mt-1 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
              />
            ) : (
              <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-900 min-h-[100px]">
                {project.description || "No description provided"}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Created: {project.created_at ? new Date(project.created_at).toLocaleDateString() : "Unknown"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building2 className="h-4 w-4" />
              <span>Updated: {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : "Unknown"}</span>
            </div>
          </div>

          {/* Location Display */}
          {project.latitude && project.longitude && (
            <div className="pt-4 border-t border-gray-200">
              <Label className="text-sm font-medium text-gray-700">Location Coordinates</Label>
              <div className="mt-1 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center gap-2 text-blue-800">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {parseFloat(project.latitude).toFixed(6)}, {parseFloat(project.longitude).toFixed(6)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            {editing ? (
              <>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setEditing(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Edit Project
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Warning Note */}
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Important Notes:</p>
              <ul className="mt-1 list-disc list-inside space-y-1">
                <li>Project name cannot be modified once created</li>
                <li>Projects cannot be deleted from the system</li>
                <li>Coordinate changes will affect any associated vehicle deployments</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
